import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { EasyNotificationService } from 'src/app/services/easy-notification.service';
import { HandleErrorService } from 'src/app/services/handle-error.service';
import { ProductBatchApi } from 'src/app/services/api-service/stocks/product-batch-api';
import { InactiveBatchDto } from '../models/product-batch';

// 勾選列 (閒置批號 + 勾選狀態)
interface StopRow extends InactiveBatchDto {
  selected: boolean;
}

/**
 * 閒置批號停用建議清單 (可嵌入):
 * 列出「未停用、建檔滿 3 個月、期間無單據異動且庫存量為 0」的批號, 勾選後可批次停用 (預設全勾選)。
 * 用於: 批號明細維護進頁的停用提醒 dialog、庫存警示 dialog 的「閒置批號」頁籤。
 */
@Component({
  selector: 'app-stocks-product-batch-inactive-list',
  templateUrl: './product-batch-inactive-list.component.html',
  styleUrls: ['./product-batch-inactive-list.component.css']
})
export class ProductBatchInactiveListComponent implements OnChanges {

  /** 轉 true 時載入/重建勾選列 (dialog 開啟時機) */
  @Input() isActive = false;

  /** 父層已取得的閒置批號清單 (null=自行呼叫 API 載入) */
  @Input() items: InactiveBatchDto[] = null;

  /** 是否顯示內建「停用已勾選批號」按鈕 (dialog 包裝時按鈕放 dialog-actions, 設 false 由外部呼叫 onConfirmStop) */
  @Input() showStopButton = true;

  /** 是否限制表格高度並內部捲動 (停用提醒 dialog 用; 庫存警示頁籤的 dialog 有自身捲軸, 保持 false 避免雙捲軸) */
  @Input() limitGridHeight = false;

  /** 停用成功後發出剩餘筆數 (父層更新徽章或關窗) */
  @Output() stopped = new EventEmitter<number>();

  rows: StopRow[] = [];

  isLoading = false;

  // 停用中 flag (防重複點擊)
  isSaving = false;

  // 表頭全選 checkbox 狀態與已勾筆數 (快取避免模板做 filter)
  _allSelected = false;
  _someSelected = false;
  _selectedCount = 0;

  // 今天 0 點 (建構時算一次, 供 isExpired 每列比對)
  private readonly today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  constructor(
    private notification: EasyNotificationService,
    private handleErrorService: HandleErrorService,
    private productBatchApi: ProductBatchApi,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && changes.isActive.currentValue === true) {
      this.init();
    }
  }

  // 載入/重建勾選列 (isActive 轉 true 時)
  private async init() {
    this.isSaving = false;
    if (this.items != null) {
      this.buildRows(this.items);
      return;
    }
    this.isLoading = true;
    try {
      const items = await this.productBatchApi.GetInactiveBatches();
      this.buildRows(items ?? []);
    } catch (ex) {
      this.buildRows([]);
      this.notification.showGetDataFailed('閒置批號資料');
    } finally {
      this.isLoading = false;
    }
  }

  // 重建勾選列, 預設全勾選
  private buildRows(items: InactiveBatchDto[]) {
    this.rows = (items ?? []).map(x => ({ ...x, selected: true }));
    this.updateHeaderCheckState();
  }

  onSelectChange() {
    this.updateHeaderCheckState();
  }

  // 表頭全選 checkbox 切換
  toggleSelectAll(checked: boolean) {
    this.rows.forEach(r => r.selected = checked);
    this.updateHeaderCheckState();
  }

  // 重算表頭 checkbox 狀態與已勾筆數
  private updateHeaderCheckState() {
    this._selectedCount = this.rows.filter(r => r.selected).length;
    this._allSelected = this.rows.length > 0 && this._selectedCount === this.rows.length;
    this._someSelected = this._selectedCount > 0 && this._selectedCount < this.rows.length;
  }

  // 有效日期已過期 (< 今天) → 紅字 (同批號明細列表的標示)
  isExpired(row: StopRow): boolean {
    if (!row.ExpirationDate) {
      return false;
    }
    const exp = new Date(row.ExpirationDate);
    return new Date(exp.getFullYear(), exp.getMonth(), exp.getDate()) < this.today;
  }

  // 停用勾選批號 (dialog 包裝模式由外部按鈕呼叫)
  async onConfirmStop() {
    if (this._selectedCount === 0) {
      this.notification.showWarning('請勾選要停用的批號');
      return;
    }
    const ids = this.rows.filter(r => r.selected).map(r => r.Id);
    this.isSaving = true;
    try {
      await this.productBatchApi.BatchStop(ids);
      this.notification.showSuccess('停用成功');
      // 移除已停用列 (頁籤模式續顯剩餘; dialog 包裝模式父層收到事件會關窗)
      this.rows = this.rows.filter(r => !r.selected);
      this.updateHeaderCheckState();
      this.stopped.emit(this.rows.length);
    } catch (ex) {
      this.notification.showError(this.handleErrorService.handleError(ex));
    } finally {
      this.isSaving = false;
    }
  }
}
